import { computed, type ComputedRef } from 'vue'
import type { StyleProps } from './use-section-props'
import type { ImageConfig } from './use-section-image'
import { addUnit } from './use-style-unit'

/**
 * 计算最终样式的 composable
 */
export function useSectionComputedStyles(mergedStyles: ComputedRef<StyleProps>, imageConfig: ComputedRef<ImageConfig>) {
  const computeStyles = computed(() => {
    const styles: Record<string, string> = {}
    const styleProps = mergedStyles.value

    // 处理背景
    if (styleProps.bgC) styles.backgroundColor = styleProps.bgC
    if (styleProps.bgI) {
      // 如果有bgI 默认写一个方法获取width height
      if (imageConfig.value.url) {
        styles.backgroundImage = `url(${imageConfig.value.url})`
      } else {
        styles.backgroundImage = `url(${styleProps.bgI})`
      }
    }
    if (styleProps.bgS) styles.backgroundSize = styleProps.bgS
    if (styleProps.bgP) styles.backgroundPosition = styleProps.bgP || 'center'
    if (styleProps.bgR) styles.backgroundRepeat = styleProps.bgR || 'no-repeat'

    // 处理尺寸
    if (imageConfig.value.width && imageConfig.value.height) {
      styles.width = addUnit(Number(imageConfig.value.width) / Number(imageConfig.value.multiple))
      styles.height = addUnit(Number(imageConfig.value.height) / Number(imageConfig.value.multiple))
    }

    if (styleProps.w) styles.width = addUnit(styleProps.w)
    if (styleProps.h) styles.height = addUnit(styleProps.h)
    if (styleProps.wh) {
      const whString = String(styleProps.wh).trim()
      const parts = whString.split(/\s+/).filter(part => part.length > 0)
      if (parts.length >= 2) {
        const [width, height] = parts
        styles.width = addUnit(width)
        styles.height = addUnit(height)
      } else if (parts.length === 1) {
        // 如果只有一个值，同时设置宽高
        const value = parts[0]
        styles.width = addUnit(value)
        styles.height = addUnit(value)
      }
    }

    // 处理字体 f='16 #fff 600' => fontSize: genVW(16) color: #fff fontWeight: 600
    if (styleProps.f) {
      const [fontSize, color, fontWeight] = styleProps.f.trim().split(' ')

      styles.fontSize = addUnit(fontSize)
      styles.color = color
      styles.fontWeight = String(fontWeight)
    }
    if (styleProps.fs) styles.fontSize = addUnit(styleProps.fs)
    if (styleProps.fc) styles.color = styleProps.fc
    if (styleProps.fw) styles.fontWeight = String(styleProps.fw)

    // 处理边框
    if (styleProps.b) styles.border = styleProps.b
    if (styleProps.bw) styles.borderWidth = addUnit(styleProps.bw)
    if (styleProps.bs) styles.borderStyle = styleProps.bs
    if (styleProps.bc) styles.borderColor = styleProps.bc

    // 处理间距
    if (styleProps.m) styles.margin = addUnit(styleProps.m)
    if (styleProps.mt) styles.marginTop = addUnit(styleProps.mt)
    if (styleProps.ml) styles.marginLeft = addUnit(styleProps.ml)
    if (styleProps.mr) styles.marginRight = addUnit(styleProps.mr)
    if (styleProps.mb) styles.marginBottom = addUnit(styleProps.mb)

    if (styleProps.pd) styles.padding = addUnit(styleProps.pd)
    if (styleProps.pdt) styles.paddingTop = addUnit(styleProps.pdt)
    if (styleProps.pdl) styles.paddingLeft = addUnit(styleProps.pdl)
    if (styleProps.pdr) styles.paddingRight = addUnit(styleProps.pdr)
    if (styleProps.pdb) styles.paddingBottom = addUnit(styleProps.pdb)

    // 处理布局
    if (styleProps.ta) styles.textAlign = styleProps.ta
    if (styleProps.lh) styles.lineHeight = addUnit(styleProps.lh)
    if (styleProps.bdr) styles.borderRadius = addUnit(styleProps.bdr)
    if (styleProps.bd) styles.border = styleProps.bd
    if (styleProps.bsw) styles.boxShadow = styleProps.bsw

    // 处理定位
    if (styleProps.p) styles.position = styleProps.p
    if (styleProps.pz) styles.zIndex = String(styleProps.pz)
    if (styleProps.ps) {
      const [position, ...rest] = String(styleProps.ps).split(' ')
      styles.position = position
      if (rest.length > 0) {
        const [top, right, bottom, left] = rest
        if (top) styles.top = addUnit(top)
        if (right) styles.right = addUnit(right)
        if (bottom) styles.bottom = addUnit(bottom)
        if (left) styles.left = addUnit(left)
      }
    }
    if (styleProps.pt) styles.top = addUnit(styleProps.pt)
    if (styleProps.pb) styles.bottom = addUnit(styleProps.pb)
    if (styleProps.pl) styles.left = addUnit(styleProps.pl)
    if (styleProps.pr) styles.right = addUnit(styleProps.pr)

    // 处理居中 center 属性设置  pc='x' 水平居中  pc='y' 垂直居中  pc='pc' 水平垂直居中
    if (styleProps.pc === 'x') {
      styles.left = '50%'
      styles.transform = 'translateX(-50%)'
    } else if (styleProps.pc === 'y') {
      styles.top = '50%'
      styles.transform = 'translateY(-50%)'
    } else if (styleProps.pc === 'pc') {
      styles.left = '50%'
      styles.top = '50%'
      styles.transform = 'translate(-50%, -50%)'
    }

    return styles
  })

  return {
    computeStyles,
  }
}
